<app-pan-action-bar class="mb-2">
  <swal
    #archiveLocationSwal
    title="Archive Location?"
    text="Are you sure you want to archive this location? This will disconnect your cameras and kiosks from this location."
    icon="warning"
    confirmButtonText="Yes, Archive"
    [showCancelButton]="true"
    [focusCancel]="true"
    (confirm)="archive()"
  >
  </swal>

  <ol class="inline-flex items-center space-x-1 md:space-x-2">
    <li>
      <a
        panButton
        panRole
        [roles]="['admin']"
        [routerLink]="['/home', 'manage', 'branch', branchId(), 'location', locationId()]"
        [queryParams]="{ subpage: 'change-history' }"
        icon="history"
        matTooltip="View History"
      >
        History
      </a>
    </li>

    @if (showSnapshotDetailsButton()) {
      <li>
        <a
          panButton
          panRole
          [roles]="['admin', 'branch-owner', 'branch-manager', 'market-manager', 'market-health-manager']"
          [routerLink]="['/home', 'manage', 'branch', branchId(), 'location', locationId()]"
          [queryParams]="{ subpage: 'market-snapshot', date: snapshotDate }"
          icon="photo_camera"
          matTooltip="View Market Snapshot"
        >
          Snapshot Details
        </a>
      </li>
    }
    @if (showAssetRecoveryButton()) {
      <li>
        <a
          panButton
          panRole
          [roles]="['admin', 'branch-owner', 'branch-manager', 'market-manager']"
          [routerLink]="['/home', 'manage', 'branch', branchId(), 'location', locationId()]"
          [queryParams]="{ subpage: 'asset-recovery' }"
          icon="qr_code"
          matTooltip="View Asset Recovery"
        >
          Asset Recovery
        </a>
      </li>
    }

    <li>
      <app-pan-button
        color="warning"
        [swal]="archiveLocationSwal"
        panRole
        [roles]="['admin', 'installer']"
        [disabled]="isLoadingLocationData() || disableActions()"
        >Archive</app-pan-button
      >
    </li>

    <li>
      @if (canAccessLocationEdit()) {
        <app-pan-button color="info" (click)="editLocation.show()" [disabled]="isLoadingLocationData() || disableActions()"
          >Edit</app-pan-button
        >
      }
    </li>
  </ol>
</app-pan-action-bar>

@if (isLoadingLocationData()) {
  <app-pan-blank-slate>
    <app-pan-spinner size="lg"></app-pan-spinner>
  </app-pan-blank-slate>
} @else if (!canAccessLocationEdit()) {
  <app-pan-blank-slate>You do not have permission to edit this location.</app-pan-blank-slate>
} @else {
  @if (locationCardSubtitle(); as subtitle) {
    <div class="my-2">
      <app-pan-chip color="info">{{ subtitle }}</app-pan-chip>
    </div>
  }

  <app-pan-section-header class="w-full mb-2" [title]="'Attributes'"></app-pan-section-header>

  <div class="grid grid-cols-3 gap-2">
    <label matTooltip="Whether or not this location has a restitution agreement">
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeRA"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('has_restitution_agreement', $event)"
      />

      Restitution Agreement
    </label>

    <label matTooltip="Whether or not this location is a dining location">
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeDL"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('is_dining_location', $event)"
      />

      Dining Location
    </label>

    <label matTooltip="Whether or not this location has a limited bandwidth connection">
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeCC"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('cellular_connection', $event)"
      />

      Cellular Connection
    </label>

    <label matTooltip="Whether or not to send daily incident summaries for this location" panRole [roles]="['admin']">
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeDIC"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('daily_incident_summary', $event)"
      />

      Daily Incident Summary

      <app-pan-button size="sm" icon="newspaper" color="info" (click)="dailyIncidentModal.show()"></app-pan-button>
    </label>

    <label matTooltip="If enabled, allows this location to capture Market Snapshots" panRole [roles]="['admin']">
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeMS"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('market_snapshots_enabled', $event)"
      />

      Market Snapshots
    </label>

    <label matTooltip="If enabled, allows this location to recover lost or stolen assets" panRole [roles]="['admin']">
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeAR"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('asset_recovery_enabled', $event)"
      />

      Asset Recovery
    </label>

    <label
      matTooltip="If enabled, allows this location's low risk cancellations to be reviewed by investigators"
      panRole
      [roles]="['admin']"
    >
      <input
        type="checkbox"
        panCheckbox
        [(ngModel)]="locationAttributeLRCR"
        [disabled]="disableActions()"
        (ngModelChange)="updateLocationAttribute('low_risk_cancellation_review_enabled', $event)"
      />

      Low Risk Cancellation Review
    </label>
  </div>

  <app-pan-modal
    #dailyIncidentModal
    [topLevel]="false"
    [shouldBodyScroll]="false"
    size="sm"
    (modalOpen)="onDailyIncidentModalOpen()"
    (modalClose)="onDailyIncidentModalClose()"
  >
    <div header>Daily Incident Recipients</div>
    <div body>
      <ng-select
        class="w-full mb-2"
        placeholder="Select recipients..."
        [items]="dailyIncidentOptions"
        [bindLabel]="'text'"
        [bindValue]="'value'"
        [multiple]="true"
        [virtualScroll]="true"
        [(ngModel)]="dailyIncidentSelections"
      >
      </ng-select>
    </div>
    <div footer>
      <app-pan-button color="primary" (click)="confirmDailyIncidentDetails()"> Confirm Recipients</app-pan-button>
    </div>
  </app-pan-modal>

  <app-pan-modal #newCameraModal [topLevel]="false" [shouldBodyScroll]="false" size="md" (modalWillClose)="unloadViewCamera()">
    <div header>Add New Camera</div>
    <div body>
      @let cameraUrl = viewCameraUrl();

      @if (allAddableCameras().length > 0) {
        <ng-select
          class="w-full mb-2"
          placeholder="Select camera..."
          [items]="sortedAddableCameras()"
          [bindLabel]="'camera_name'"
          [virtualScroll]="true"
          [(ngModel)]="locationAddCamera"
          typeToSearchText="Please enter 3 or more characters"
        >
          <ng-template ng-option-tmp let-item="item">
            <div class="flex justify-between">
              {{ item.camera_name }}

              <app-pan-video-status
                class="absolute right-2"
                [status]="item.status === 'GREEN' ? 'healthy' : 'unhealthy'"
              ></app-pan-video-status>
            </div>
          </ng-template>
        </ng-select>
      } @else {
        <app-pan-inline-alert type="warning">There are no available cameras to assign to this location.</app-pan-inline-alert>
      }

      @if (locationAddCamera()) {
        @if (!cameraUrl) {
          <app-pan-blank-slate>
            <app-pan-spinner size="lg"></app-pan-spinner>
          </app-pan-blank-slate>
        } @else if (cameraUrl === "error") {
          <app-pan-inline-alert [type]="'danger'"
            >Could not load data from this camera. You can still add it, but there is no preview of it available at this
            time.</app-pan-inline-alert
          >
        } @else {
          <app-pan-section-header class="mb-2" [title]="'Camera Snapshot Preview'"></app-pan-section-header>
          <img [src]="cameraUrl" alt="camera preview for {{ viewCamera()?.id }}" />
        }
      }
    </div>

    <div footer class="flex gap-2">
      <app-pan-button panModalClose>Cancel</app-pan-button>
      <app-pan-button panModalClose color="primary" (click)="addNewCamera()" [disabled]="!locationAddCamera() || disableActions()"
        >Add Camera</app-pan-button
      >
    </div>
  </app-pan-modal>

  <app-pan-section-header class="mt-4" title="Camera Configurations">
    <div action class="flex gap-2">
      <app-pan-help
        size="md"
        helpText="Camera names are synced with Rhombus in the background and might not be up to date after adding or removing them. Refresh to get latest updates."
      ></app-pan-help>
      <app-pan-button [disabled]="disableActions()" icon="add" color="info" (click)="newCameraModal.show()"></app-pan-button>
    </div>
  </app-pan-section-header>

  <ag-grid-angular [rowData]="cameras()" [columnDefs]="cameraColumns" [gridOptions]="cameraTable"></ag-grid-angular>

  <app-pan-modal #newKioskModal [topLevel]="false" [shouldBodyScroll]="false" size="md" (modalWillClose)="unsetSelectedKiosk()">
    <div header>Add New Kiosk</div>
    <div body class="max-h-[500px] min-h-[250px] overflow-scroll">
      @if (allAddableKiosks().length > 0) {
        <ng-select
          class="w-full mb-2"
          placeholder="Select platform..."
          [items]="branchAliases()"
          [bindLabel]="'display_name'"
          [bindValue]="'branch_alias_id'"
          [virtualScroll]="true"
          [(ngModel)]="selectedBranchAliasId"
          (ngModelChange)="selectedKiosk.set(undefined)"
          typeToSearchText="Please enter 3 or more characters"
        >
          <ng-template ng-option-tmp let-item="item">
            <div class="flex items-center">
              <span>{{ item.display_name }}</span>
            </div>
          </ng-template>
        </ng-select>

        <ng-select
          class="w-full mb-2"
          placeholder="Select kiosk..."
          [items]="sortedAddableKiosks()"
          [bindLabel]="'name'"
          [virtualScroll]="true"
          [(ngModel)]="selectedKiosk"
          typeToSearchText="Please enter 3 or more characters"
        >
          <ng-template ng-option-tmp let-item="item">
            <div class="flex items-center">
              <span>{{ `${item.name} - ${item.external_id ?? '-'}${item.unit_type ? ` (${item.unit_type})` : ''}` }}</span>
            </div>
          </ng-template>
        </ng-select>
      } @else {
        <app-pan-inline-alert type="warning">There are no available kiosks to assign to this location.</app-pan-inline-alert>
      }

      @if (selectedKiosk() && selectedKioskTransactions()) {
        @if (loadingKioskTransactions()) {
          <app-pan-blank-slate>
            <app-pan-spinner size="lg"></app-pan-spinner>
          </app-pan-blank-slate>
        } @else {
          @if (selectedKioskTransactions().length === 0) {
            <app-pan-inline-alert type="warning">This kiosk has no transactions.</app-pan-inline-alert>
          } @else {
            <app-pan-section-header class="mb-2" [title]="'Kiosk Transaction Preview'"></app-pan-section-header>
          }
        }

        @for (trans of selectedKioskTransactions(); track $index) {
          <app-pan-card>
            <div class="font-bold relative min-h-8 flex items-center">
              {{ trans.trans_id }}
              <app-pan-transaction-type
                [state]="trans.type === 'sold' ? 'Sold' : 'Cancel'"
                class="absolute right-[-16px]"
              ></app-pan-transaction-type>
            </div>
            <div>{{ trans.product_name }}</div>
            <div class="italic text-sm">{{ trans.trans_date | date: "medium" }}</div>
          </app-pan-card>
        }
      }
    </div>

    <div footer class="flex gap-2">
      <app-pan-button panModalClose>Cancel</app-pan-button>
      <app-pan-button color="primary" (click)="addNewKiosk()" [disabled]="!selectedKiosk() || disableActions()">Add Kiosk</app-pan-button>
    </div>
  </app-pan-modal>

  <app-pan-section-header class="mt-4" title="Kiosk Configurations">
    <app-pan-button [disabled]="disableActions()" action icon="add" color="info" (click)="openNewKioskModal()"></app-pan-button>
  </app-pan-section-header>

  <ag-grid-angular [rowData]="kiosks()" [columnDefs]="kioskColumns" [gridOptions]="kioskTable"></ag-grid-angular>

  <app-pan-modal #newTerminalModal [topLevel]="false" [shouldBodyScroll]="false" size="md" (modalWillClose)="clearTerminalValues()">
    <div header>Add New Terminal</div>
    <div body class="max-h-[500px] min-h-[250px] overflow-scroll">
      <app-pan-form-control>
        <app-pan-floating-label>Terminal Name</app-pan-floating-label>
        <input panInput [(ngModel)]="newTerminalName" placeholder="Terminal name..." />
      </app-pan-form-control>

      <app-pan-form-control>
        <app-pan-floating-label>Terminal ID</app-pan-floating-label>
        <input panInput [(ngModel)]="newTerminalId" placeholder="Terminal ID..." />
      </app-pan-form-control>
    </div>

    <div footer class="flex gap-2">
      <app-pan-button panModalClose>Cancel</app-pan-button>
      <app-pan-button color="primary" (click)="addNewTerminal()" [disabled]="disableActions()">Add Terminal</app-pan-button>
    </div>
  </app-pan-modal>

  <app-pan-section-header class="mt-4" title="Terminal Configurations">
    <app-pan-button [disabled]="disableActions()" action icon="add" color="info" (click)="newTerminalModal.show()"></app-pan-button>
  </app-pan-section-header>

  <ag-grid-angular [rowData]="terminals()" [columnDefs]="terminalColumns" [gridOptions]="terminalTable"></ag-grid-angular>
}

<app-pan-modal #cameraDisplayCheck size="sm" [topLevel]="true" [shouldBodyScroll]="false" (modalWillClose)="unloadViewCamera()">
  <div header>Camera Preview</div>
  <div body>
    @let cameraUrl = viewCameraUrl();

    @if (!cameraUrl) {
      <app-pan-blank-slate>
        <app-pan-spinner size="lg"></app-pan-spinner>
      </app-pan-blank-slate>
    } @else if (cameraUrl === "error") {
      <app-pan-inline-alert [type]="'danger'">Could not load preview for this camera.</app-pan-inline-alert>
    } @else {
      <app-pan-section-header class="mb-2" [title]="'Camera Snapshot Preview'"></app-pan-section-header>
      <img [src]="cameraUrl" alt="camera preview for {{ viewCamera()?.camera_name }}" />
    }
  </div>
  <div footer>
    <app-pan-button panModalClose>Close</app-pan-button>
  </div>
</app-pan-modal>

<app-pan-modal #editLocation>
  <div header data-testid="image-preview-modal-title">Edit Location</div>
  <div body>
    <app-pan-form-control>
      <app-pan-floating-label>Location Name</app-pan-floating-label>
      <input panInput [(ngModel)]="locationName" placeholder="Location name..." />
    </app-pan-form-control>

    <app-pan-form-control>
      <app-pan-floating-label>Location Timezone</app-pan-floating-label>
      <ng-select
        class="w-full mb-2"
        placeholder="Select timezone..."
        [items]="allTimezones()"
        [(ngModel)]="locationTimezone"
        [clearable]="false"
      >
      </ng-select>
    </app-pan-form-control>

    <app-pan-form-control>
      <app-pan-floating-label>External ID</app-pan-floating-label>
      <input panInput [(ngModel)]="locationExternalId" placeholder="Set External ID..." />
    </app-pan-form-control>
  </div>

  <div footer class="flex gap-2">
    <app-pan-button panModalClose>Close</app-pan-button>
    <app-pan-button color="primary" panModalClose (click)="save()" [disabled]="!canSave() || disableActions()">Save</app-pan-button>
  </div>
</app-pan-modal>
